import every from 'lodash/every';
import isEqual from 'lodash/isEqual';
import keys from 'lodash/keys';

import { ColumnType, TRowModel, SelectMode, RowSelection, SelectionState } from './types';

export const horizontalContentAlignmentMap = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
} as const;

export const verticalContentAlignmentMap = {
  baseline: 'baseline',
  top: 'flex-start',
  middle: 'center',
  bottom: 'flex-end',
} as const;

/**
 * Determines the selected status of a table row based on the provided row selections and row keys.
 *
 * @param rows - The main row object containing sub-rows.
 * @returns Returns 'all' if all rows are selected, 'partial' if some rows are selected, and false if no rows are selected.
 */
export function getAllSubRowsMatchRow<Row>(row: Row & TRowModel<Row>) {
  const { subRows, ...rest } = row;

  return every(subRows, (subRow) => {
    return isEqual(keys(rest).sort(), keys(subRow).sort());
  });
}

/**
 * Calculates the value for the CSS `grid-template-columns` property based on the configuration of a data table and the columns provided.
 *
 * @param tableRowConfig - The configuration object for the data table.
 * @param columns - An array of column objects.
 * @returns The calculated value for the CSS `grid-template-columns` property.
 */
export function getGridTemplateColumns<Row extends TRowModel<Row>>(
  hasDragDropAndSelectColumn: boolean,
  hasMenuExpandColumn: boolean,
  columns: ColumnType<Row>[],
): string {
  let gridTemplateColumns = hasDragDropAndSelectColumn ? 'max-content' : '';

  // Check the width type and append the appropriate value to the gridTemplateColumns.
  columns.forEach((column) => {
    const { width } = column;
    if (typeof width === 'object' && width !== null) {
      const max = width.max === '1fr' ? '1fr' : `${width.max}px`;
      gridTemplateColumns += ` minmax(${width.min}px, ${max})`;
    } else {
      gridTemplateColumns +=
        typeof width === 'number' ? ` ${width}px` : width === 'auto' ? ' auto' : ' 1fr';
    }
  });

  gridTemplateColumns += hasMenuExpandColumn ? ' auto' : '';

  return gridTemplateColumns;
}

export function getSubRowSelectionStatus<Row>(
  rowSelections: Record<string, SelectMode>,
  record: TRowModel<Row>,
): SelectMode {
  const subRows = record.subRows;
  if (!subRows || subRows.length === 0) {
    return 'NONE';
  }

  if (subRows.every((subRow) => rowSelections[subRow.id] === 'ALL')) {
    return 'ALL';
  }

  if (subRows.every((subRow) => rowSelections[subRow.id] === 'NONE')) {
    return 'NONE';
  }

  if (subRows.some((subRow) => rowSelections[subRow.id] === 'ALL')) {
    return 'SOME';
  }

  return 'NONE';
}

export function getRowSelectionStatus<Row>(
  rowSelections: Record<string, SelectMode>,
  record: TRowModel<Row>,
  parentRow?: TRowModel<Row>,
) {
  if (parentRow && rowSelections[parentRow.id] === 'ALL') {
    return 'ALL';
  }

  if (rowSelections[record.id] === 'ALL') {
    return 'ALL';
  }

  if (record.subRows) {
    return getSubRowSelectionStatus(rowSelections, record);
  }

  return 'NONE';
}

export function getSelectionState(
  selectedRows: 'ALL' | string[] | undefined,
  rowIds: Set<string>,
  tableSelectMode: SelectionState,
) {
  const newSelections: Record<string, SelectMode> = {};

  if (!selectedRows || !selectedRows.length) {
    return newSelections;
  }

  const isAllSelected = selectedRows === 'ALL';

  rowIds.forEach((id) => {
    const shouldSelectRow = tableSelectMode.bulkSelect && !tableSelectMode.unselected.includes(id);

    let selectionValue: SelectMode = 'NONE';

    if (isAllSelected || shouldSelectRow || selectedRows.includes(id)) {
      selectionValue = 'ALL';
    }

    newSelections[id] = selectionValue;
  });

  return newSelections;
}

export function getRowIds<Row>(
  data: TRowModel<Row>[],
  isRowDisabled?: (row: Row) => boolean,
): {
  allRowIds: Set<string>;
  subRowIds: Set<string>;
} {
  const allRowIds = new Set<string>();
  const subRowIds = new Set<string>();

  const addRowId = (row: TRowModel<Row>) => {
    if (isRowDisabled && isRowDisabled(row as Row)) {
      return;
    }

    if (!row.subRows || row.subRows.length === 0) {
      allRowIds.add(row.id);
    } else {
      row.subRows.forEach((subRow) => {
        subRowIds.add(subRow.id);
        addRowId(subRow);
      });
    }
  };

  data.forEach(addRowId);

  return { allRowIds, subRowIds };
}

/**
 * Generates a list of row selections based on the current selections and the provided data.
 *
 * @param data
 * @param currentSelections
 * @returns Array of RowSelection objects indicating which rows and sub-rows are selected.
 */
export function getRowSelections<Row>(
  data: TRowModel<Row>[],
  currentSelectedIds: string[],
): RowSelection[] {
  const selectedIds = new Set(currentSelectedIds);
  const rowSelections: RowSelection[] = [];

  data.forEach(({ id, subRows }) => {
    if (!subRows?.length && selectedIds.has(id)) {
      rowSelections.push({ rowId: id, selectedSubrows: undefined });
      return;
    }

    const selectedSubrows = subRows
      ?.filter((subRow) => selectedIds.has(subRow.id))
      .map((subRow) => subRow.id);

    if (selectedSubrows && selectedSubrows.length > 0) {
      rowSelections.push({ rowId: id, selectedSubrows });
    }
  });

  return rowSelections;
}

/**
 * Updates the selection state for a given row and its sub-rows.
 *
 * If toggled row has parent, only the row's selection state is updated, ignoring parent state.
 * If toggled row has sub-rows, update its selection state and that of all sub-rows.
 * If toggled row has no sub-rows, only the row's selection state is updated.
 *
 * @param row
 * @param value
 * @param selectionState
 * @param parentRow
 *
 * @returns Returns an updated selection state on row toggle
 */
export function getUpdatedSelection<Row>(
  row: TRowModel<Row>,
  value: SelectMode,
  selectionState: Record<string, SelectMode>,
  parentRow: TRowModel<Row> | undefined,
): Record<string, SelectMode> {
  const updatedSelections = { ...selectionState };

  if (parentRow) {
    updatedSelections[row.id] = value;
  }

  if (!row.subRows || row.subRows.length === 0) {
    updatedSelections[row.id] = value;
  }

  row.subRows?.forEach((subRow) => (updatedSelections[subRow.id] = value));

  return updatedSelections;
}

export function groupBy<T>(array: T[], keyGetter: (item: T) => string): Record<string, T[]> {
  return array.reduce(
    (arr, curr) => {
      const key = keyGetter(curr);

      if (!arr[key]) {
        arr[key] = [];
      }
      arr[key].push(curr);
      return arr;
    },
    {} as Record<string, T[]>,
  );
}
